// src/pages/transactions/TransactionDetailsModal.jsx
"use client";

import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { motion } from "framer-motion";
import {
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  DollarSign,
  Calendar,
  User,
  Globe,
  FileText,
  CreditCard,
  TrendingUp,
  Info,
  Target,
  ArrowDownLeft,
  ArrowUpRight,
  Hash,
} from "lucide-react";
import formatDateGlobal from "../../utils/Functions/formatDateGlobal";

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const TransactionDetailsModal = ({ isOpen, onClose, transaction, currentUser }) => {
  if (!transaction) return null;

  /* ========= NORMALIZED DATA / LOGIC ========= */

  const isClient = (currentUser?.role?.name || "").toLowerCase() === "client";
  const currency = transaction.currency || "USD";

  const formatCurrency = (amount, curr = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr === "USDT" ? "USD" : curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeNumber(amount));

  const formatPercentage = (pct) => {
    const n = safeNumber(pct);
    const fixed = n.toFixed(2).replace(/\.00$/, "");
    return `${fixed}%`;
  };

  const typeRaw = transaction.type || "";
  const typeLower = typeRaw.toLowerCase();

  const isTopUp =
    typeLower === "top-up" ||
    typeLower === "topup" ||
    typeLower === "deposit";

  const isTopDown =
    typeLower === "top-down" ||
    typeLower === "topdown" ||
    typeLower === "withdrawal";

  const mainTitle = isTopUp
    ? "Top-Up details"
    : isTopDown
      ? "Withdraw details"
      : "Transfer details";

  // DB note:
  // source_account = Credit, destination_account = Ad (even for withdraw).
  // For display on withdraw, swap.
  const creditAccountObj = transaction.source_account || null;
  const adAccountObj = transaction.destination_account || null;

  const sourceAccountObj = isTopDown ? adAccountObj : creditAccountObj;
  const destinationAccountObj = isTopDown ? creditAccountObj : adAccountObj;

  const directionLabel = (() => {
    const creditName = creditAccountObj?.account_name;
    const adName = adAccountObj?.account_name;

    if (isTopUp) {
      return `Top-Up from credit account "${creditName || "-"}" to ad account "${adName || "-"}"`;
    }
    if (isTopDown) {
      return `Withdraw from ad account "${adName || "-"}" back to credit account "${creditName || "-"}"`;
    }
    return `Transfer between "${creditName || "-"}" and "${adName || "-"}"`;
  })();

  // ✅ Fees (support both old + new)
  const clientFeeAmount = safeNumber(
    transaction.client_fee_amount ?? transaction.fees_amount
  );
  const clientFeePct = safeNumber(
    transaction.client_fee_percentage ?? transaction.fee_percentage
  );

  const providerFeeAmount = safeNumber(transaction.provider_fee_amount);
  const providerFeePct = safeNumber(transaction.provider_fee_percentage);

  const profitAmount =
    transaction.profit_amount != null
      ? safeNumber(transaction.profit_amount)
      : clientFeeAmount - providerFeeAmount;

  const weberzFeePct = Math.max(0, safeNumber(clientFeePct) - safeNumber(providerFeePct));

  // ✅ provider name always from AdAccount
  const providerName =
    adAccountObj?.provider?.name ||
    transaction?.destination_account?.provider?.name ||
    "-";

  // TOTAL in your table
  const totalAmount = safeNumber(transaction.amount);

  // ✅ Net value:
  // - Top-Up: net = what ad receives
  // - Withdraw: net = amount taken from ad account (client requested)
  const netFromDB = safeNumber(transaction.net_amount_received);

  const netValue = (() => {
    if (!isTopDown) {
      // Top-Up / Transfer
      return safeNumber(transaction.net_amount_received ?? transaction.amount);
    }

    // Withdraw: prefer stored net if it looks valid (<= total)
    if (netFromDB > 0 && netFromDB <= totalAmount) return netFromDB;

    // fallback: total - fee
    const computed = totalAmount - clientFeeAmount;
    return computed > 0 ? computed : 0;
  })();

  // ✅ Withdraw totals based on your definitions
  const returnedFees = isTopDown ? clientFeeAmount : 0;
  const totalWithdrawal = isTopDown ? (netValue + returnedFees) : 0;

  // ✅ Top-Up total charged label (net top-up + fees)
  // In your DB: totalAmount should already be (net + fees) for Top-Up.
  const totalChargedTopUp = isTopUp ? (netValue + clientFeeAmount) : totalAmount;

  const hasEnhancedSchema =
    transaction.client_fee_amount != null ||
    transaction.fees_amount != null ||
    transaction.net_amount_received != null ||
    transaction.provider_fee_amount != null ||
    transaction.profit_amount != null ||
    transaction.client_fee_percentage != null ||
    transaction.provider_fee_percentage != null ||
    transaction.fee_percentage != null;

  const showEnhancedView = Boolean(hasEnhancedSchema);

  // ✅ Show provider/profit for Admin/Manager too — hide only for Client
  const showProviderProfit = !isClient;

  const getClientStatusDisplay = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "completed":
        return "Completed";
      case "pending":
        return "Processing";
      case "failed":
        return "Failed";
      case "reversed":
        return "Reversed";
      default:
        return s ? s[0].toUpperCase() + s.slice(1) : "-";
    }
  };

  const statusInfo = (() => {
    const statusLower = (transaction.status || "").toLowerCase();

    switch (statusLower) {
      case "completed":
        return {
          bgClass: "bg-green-50 dark:bg-green-900/20",
          textClass: "text-green-700 dark:text-green-400",
          dotClass: "bg-green-500",
          iconClass: "text-green-600 dark:text-green-400",
        };
      case "pending":
        return {
          bgClass: "bg-amber-50 dark:bg-amber-900/20",
          textClass: "text-amber-700 dark:text-amber-400",
          dotClass: "bg-amber-500",
          iconClass: "text-amber-600 dark:text-amber-400",
        };
      case "failed":
        return {
          bgClass: "bg-red-50 dark:bg-red-900/20",
          textClass: "text-red-700 dark:text-red-400",
          dotClass: "bg-red-500",
          iconClass: "text-red-600 dark:text-red-400",
        };
      case "reversed":
        return {
          bgClass: "bg-orange-50 dark:bg-orange-900/20",
          textClass: "text-orange-700 dark:text-orange-400",
          dotClass: "bg-orange-500",
          iconClass: "text-orange-600 dark:text-orange-400",
        };
      default:
        return {
          bgClass: "bg-neutral-50 dark:bg-neutral-900/40",
          textClass: "text-neutral-700 dark:text-neutral-400",
          dotClass: "bg-neutral-500",
          iconClass: "text-neutral-600 dark:text-neutral-400",
        };
    }
  })();

  const getPlatformColor = (platform) => {
    switch ((platform || "").toLowerCase()) {
      case "meta":
        return "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
      case "google":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400";
      case "tiktok":
        return "bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400";
      case "snapchat":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-400";
    }
  };

  const typePillClasses = (() => {
    if (isTopUp)
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
    if (isTopDown)
      return "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
    return "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
  })();

  const platformLabel = isTopDown
    ? (transaction.source_platform || adAccountObj?.platform || "-")
    : (transaction.destination_platform ||
      adAccountObj?.platform ||
      transaction.source_platform ||
      creditAccountObj?.platform ||
      "-");

  const netLabel = isTopUp ? "Net top-up" : isTopDown ? "Net Withdrawal" : "Net amount";
  const netHelperText = isTopUp
    ? "Net amount added to the ad account"
    : isTopDown
      ? "Amount taken from the ad account"
      : "Amount after all fees";

  // ✅ No dynamic Tailwind strings (clean + safe)
  const netTheme = (() => {
    if (isTopDown) {
      return {
        wrap: "bg-pink-50 dark:bg-pink-900/20 border border-pink-100 dark:border-pink-900",
        title: "text-pink-800 dark:text-pink-200",
        value: "text-pink-900 dark:text-pink-100",
        helper: "text-pink-700 dark:text-pink-300",
        icon: "text-pink-600 dark:text-pink-400",
      };
    }
    if (isTopUp) {
      return {
        wrap: "bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-900",
        title: "text-purple-800 dark:text-purple-200",
        value: "text-purple-900 dark:text-purple-100",
        helper: "text-purple-700 dark:text-purple-300",
        icon: "text-purple-600 dark:text-purple-400",
      };
    }
    return {
      wrap: "bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900",
      title: "text-purple-800 dark:text-purple-200",
      value: "text-purple-900 dark:text-purple-100",
      helper: "text-purple-700 dark:text-purple-300",
      icon: "text-purple-600 dark:text-purple-400",
    };
  })();

  const sourceAccountLabel = isTopUp
    ? "Source credit account"
    : isTopDown
      ? "Source ad account"
      : "Source account";

  const destinationAccountLabel = isTopUp
    ? "Destination ad account"
    : isTopDown
      ? "Destination credit account"
      : "Destination account";

  /* ========= ANIMATIONS ========= */

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.18, staggerChildren: 0.04 },
    },
    exit: { opacity: 0, scale: 0.96, transition: { duration: 0.18 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.22 } },
  };

  /* ========= RENDER ========= */

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${showEnhancedView ? "max-w-6xl" : "max-w-3xl"} transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 ${showEnhancedView ? "max-h-[90vh] overflow-y-auto" : ""}`}
              >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white/95 dark:bg-neutral-950/95 backdrop-blur border-b border-neutral-200 dark:border-neutral-900 px-6 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Dialog.Title className="text-lg font-[400] text-neutral-900 dark:text-white">
                        {mainTitle}
                      </Dialog.Title>
                      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        {directionLabel}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    {/* SUMMARY STRIP */}
                    <motion.div
                      variants={itemVariants}
                      className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/40"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full flex items-center justify-center ${statusInfo.bgClass}`}>
                          <DollarSign size={16} className={statusInfo.iconClass} />
                        </div>

                        <div className="flex-1 text-start">
                          <div className="flex flex-wrap justify-between gap-3">
                            <div>
                              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                                Transfer #{transaction.transaction_id || transaction._id}
                              </h3>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDateGlobal(transaction.createdAt)}
                              </p>
                            </div>

                            <div className="text-right">
                              <p className="text-lg font-semibold text-neutral-900 dark:text-white">
                                {isTopUp
                                  ? formatCurrency(totalChargedTopUp, currency)
                                  : formatCurrency(totalAmount, currency)}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                {isTopUp
                                  ? "Total amount charged (net top-up + fees)"
                                  : `Total amount (${currency})`}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            {/* Status pill */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                Status
                              </span>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgClass} ${statusInfo.textClass}`}
                              >
                                <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${statusInfo.dotClass}`} />
                                {isClient
                                  ? getClientStatusDisplay(transaction.status)
                                  : (transaction.status || "-").toString().charAt(0).toUpperCase() +
                                  (transaction.status || "").toString().slice(1)}
                              </span>
                            </div>

                            {/* Type pill */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                Type
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typePillClasses}`}>
                                {isTopUp ? "Top-Up" : isTopDown ? "Withdraw" : typeRaw || "-"}
                              </span>
                            </div>

                            {/* Platform pill */}
                            {platformLabel && platformLabel !== "-" && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                  Platform
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlatformColor(platformLabel)}`}>
                                  <Globe size={12} className="mr-1" />
                                  {platformLabel}
                                </span>
                              </div>
                            )}

                            {/* ✅ Provider pill (Top-Up + Withdraw, hide for Client) */}
                            {showProviderProfit && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                  Provider
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                  {providerName}
                                </span>
                              </div>
                            )}

                            {/* Reference */}
                            {transaction.reference && (
                              <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                                <Hash size={12} />
                                <span>{transaction.reference}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* ENHANCED VIEW */}
                    {showEnhancedView ? (
                      <>
                        {/* Amounts & fee breakdown */}
                        <motion.div variants={itemVariants} className="space-y-4">
                          <h3 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                            <TrendingUp size={16} />
                            Amounts & fee breakdown
                          </h3>

                          {/* ✅ Withdraw keeps old 3 cards + adds provider/profit when allowed */}
                          {isTopDown ? (
                            <div className={`grid grid-cols-1 ${showProviderProfit ? "lg:grid-cols-5" : "lg:grid-cols-3"} gap-4`}>

                              {/* Net Withdrawal */}
                              <div className={`p-4 rounded-xl ${netTheme.wrap}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Target size={14} className={netTheme.icon} />
                                  <span className={`text-xs font-medium ${netTheme.title}`}>
                                    Net Withdrawal
                                  </span>
                                </div>
                                <p className={`text-lg font-semibold ${netTheme.value}`}>
                                  {formatCurrency(netValue, currency)}
                                </p>
                                <p className={`text-[11px] mt-1 ${netTheme.helper}`}>
                                  Amount taken from the ad account
                                </p>
                              </div>



                              {/* Provider + Weberz (only not Client) */}
                              {showProviderProfit && (
                                <>
                                  <div className="p-4 rounded-xl border border-red-100 dark:border-red-900 bg-red-50 dark:bg-red-950/20">
                                    <div className="flex items-center gap-2 mb-2">
                                      <ArrowUpRight size={14} className="text-red-600 dark:text-red-400" />
                                      <span className="text-xs font-medium text-red-800 dark:text-red-200">
                                        Provider fee
                                      </span>
                                      <span className="text-xs text-red-600 dark:text-red-400">
                                        ({formatPercentage(providerFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-red-900 dark:text-red-100">
                                      {formatCurrency(providerFeeAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-red-700 dark:text-red-300 mt-1">
                                      Fees credited back to the tracking balance by the provider
                                    </p>
                                  </div>

                                  <div className="p-4 rounded-xl border border-emerald-100 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Target size={14} className="text-emerald-600 dark:text-emerald-400" />
                                      <span className="text-xs font-medium text-emerald-800 dark:text-emerald-200">
                                        Weberz fee
                                      </span>
                                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                                        ({formatPercentage(weberzFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
                                      {formatCurrency(profitAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-1">
                                      Fees credited back to the tracking balance by the agency
                                    </p>
                                  </div>
                                </>
                              )}
                              {/* Returned Fees */}
                              <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900">
                                <div className="flex items-center gap-2 mb-2">
                                  <ArrowDownLeft size={14} className="text-orange-600 dark:text-orange-400" />
                                  <span className="text-xs font-medium text-orange-800 dark:text-orange-300">
                                    Returned Fees
                                  </span>
                                  <span className="text-xs text-orange-600 dark:text-orange-400">
                                    ({formatPercentage(clientFeePct)})
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-orange-900 dark:text-orange-200">
                                  {formatCurrency(returnedFees, currency)}
                                </p>
                                <p className="text-[11px] text-orange-700 dark:text-orange-300 mt-1">
                                  Fees credited back to the tracking balance
                                </p>
                              </div>
                              {/* Total Withdrawal */}
                              <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/60">
                                <div className="flex items-center gap-2 mb-2">
                                  <DollarSign size={14} className="text-neutral-600 dark:text-neutral-400" />
                                  <span className="text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                    Total Withdrawal
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                                  {formatCurrency(totalWithdrawal, currency)}
                                </p>
                                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                                  Amount returned to the tracking balance
                                </p>
                              </div>

                            </div>
                          ) : (
                            // ✅ Top-Up / Transfer (same layout you had)
                            <div className={`grid grid-cols-1 ${showProviderProfit ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
                              {showProviderProfit ? (
                                <>
                                  {/* Net */}
                                  <div className={`p-4 rounded-xl ${netTheme.wrap}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Target size={14} className={netTheme.icon} />
                                      <span className={`text-xs font-medium ${netTheme.title}`}>
                                        {netLabel}
                                      </span>
                                    </div>
                                    <p className={`text-lg font-semibold ${netTheme.value}`}>
                                      {formatCurrency(netValue, currency)}
                                    </p>
                                    <p className={`text-[11px] mt-1 ${netTheme.helper}`}>
                                      {netHelperText}
                                    </p>
                                  </div>

                                  {/* Client fee */}
                                  <div className="p-4 rounded-xl border border-yellow-100 dark:border-yellow-900 bg-yellow-50 dark:bg-yellow-950/20">
                                    <div className="flex items-center gap-2 mb-2">
                                      <ArrowDownLeft size={14} className="text-yellow-600 dark:text-yellow-400" />
                                      <span className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
                                        Client fee
                                      </span>
                                      <span className="text-xs text-yellow-600 dark:text-yellow-400">
                                        ({formatPercentage(clientFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                                      {formatCurrency(clientFeeAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-yellow-700 dark:text-yellow-300 mt-1">
                                      Fee paid by the client
                                    </p>
                                  </div>

                                  {/* Provider fee */}
                                  <div className="p-4 rounded-xl border border-red-100 dark:border-red-900 bg-red-50 dark:bg-red-950/20">
                                    <div className="flex items-center gap-2 mb-2">
                                      <ArrowUpRight size={14} className="text-red-600 dark:text-red-400" />
                                      <span className="text-xs font-medium text-red-800 dark:text-red-200">
                                        Provider fee
                                      </span>
                                      <span className="text-xs text-red-600 dark:text-red-400">
                                        ({formatPercentage(providerFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-red-900 dark:text-red-100">
                                      {formatCurrency(providerFeeAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-red-700 dark:text-red-300 mt-1">
                                      Fee paid to the provider
                                    </p>
                                  </div>

                                  {/* Agency profit */}
                                  <div className="p-4 rounded-xl border border-emerald-100 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Target size={14} className="text-emerald-600 dark:text-emerald-400" />
                                      <span className="text-xs font-medium text-emerald-800 dark:text-emerald-200">
                                        Weberz fee
                                      </span>
                                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                                        ({formatPercentage(weberzFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
                                      {formatCurrency(profitAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-1">
                                      Fee left for the agency
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>


                                  {/* Net */}
                                  <div className={`p-4 rounded-xl ${netTheme.wrap}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Target size={14} className={netTheme.icon} />
                                      <span className={`text-xs font-medium ${netTheme.title}`}>
                                        {netLabel}
                                      </span>
                                    </div>
                                    <p className={`text-lg font-semibold ${netTheme.value}`}>
                                      {formatCurrency(netValue, currency)}
                                    </p>
                                    <p className={`text-[11px] mt-1 ${netTheme.helper}`}>
                                      {netHelperText}
                                    </p>
                                  </div>
                                  {/* Fees */}
                                  <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900">
                                    <div className="flex items-center gap-2 mb-2">
                                      <ArrowDownLeft size={14} className="text-orange-600 dark:text-orange-400" />
                                      <span className="text-xs font-medium text-orange-800 dark:text-orange-400">
                                        Fees
                                      </span>
                                      <span className="text-xs text-orange-600 dark:text-orange-400">
                                        ({formatPercentage(clientFeePct)})
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-orange-900 dark:text-orange-200">
                                      {formatCurrency(clientFeeAmount, currency)}
                                    </p>
                                    <p className="text-[11px] text-orange-700 dark:text-orange-300 mt-1">
                                      Fee paid by the client
                                    </p>
                                  </div>
                                  {/* Total charged for Top-Up */}

                                  <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/60">
                                    <div className="flex items-center gap-2 mb-2">
                                      <DollarSign size={14} className="text-neutral-600 dark:text-neutral-400" />
                                      <span className="text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                        {isTopUp ? "Total amount charged" : "Total amount"}
                                      </span>
                                    </div>
                                    <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                                      {isTopUp
                                        ? formatCurrency(totalChargedTopUp, currency)
                                        : formatCurrency(totalAmount, currency)}
                                    </p>
                                    {isTopUp && (
                                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                                        Net top-up + fees
                                      </p>
                                    )}
                                  </div>

                                </>
                              )}
                            </div>
                          )}

                        </motion.div>

                        {/* Accounts + Metadata (kept from your original) */}
                        <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="space-y-4 text-start">
                            <h3 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                              <CreditCard size={16} />
                              Accounts involved
                            </h3>

                            {/* Source */}
                            <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/40">
                              <div className="flex items-start gap-3">
                                <ArrowUpRight size={16} className="mt-1 text-neutral-500 dark:text-neutral-400" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                      {sourceAccountLabel}
                                    </p>
                                    {isTopDown && platformLabel && platformLabel !== "-" && (
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getPlatformColor(platformLabel)}`}>
                                        {platformLabel}
                                      </span>
                                    )}
                                  </div>

                                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                                    {sourceAccountObj?.account_name || "-"}
                                  </p>

                                  {sourceAccountObj?.account_id && (
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                      ID: {sourceAccountObj.account_id}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Destination */}
                            <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/40">
                              <div className="flex items-start gap-3">
                                <ArrowDownLeft size={16} className="mt-1 text-neutral-500 dark:text-neutral-400" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                      {destinationAccountLabel}
                                    </p>
                                    {!isTopDown && platformLabel && platformLabel !== "-" && (
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getPlatformColor(platformLabel)}`}>
                                        {platformLabel}
                                      </span>
                                    )}
                                  </div>

                                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                                    {destinationAccountObj?.account_name || "-"}
                                  </p>

                                  {destinationAccountObj?.account_id && (
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                      ID: {destinationAccountObj.account_id}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Additional */}
                          <div className="space-y-4 text-start">
                            <h3 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                              <Info size={16} />
                              Additional details
                            </h3>

                            <div className="space-y-3">
                              <div className="flex items-center p-3 rounded-xl bg-neutral-50 dark:bg-neutral-900/40">
                                <Calendar size={16} className="text-neutral-500 dark:text-neutral-400 mr-3" />
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                    Created at
                                  </p>
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {formatDateGlobal(transaction.createdAt)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center p-3 rounded-xl bg-neutral-50 dark:bg-neutral-900/40">
                                <Hash size={16} className="text-neutral-500 dark:text-neutral-400 mr-3" />
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                    Operation ID
                                  </p>
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {transaction.reference || "-"}
                                  </p>
                                </div>
                              </div>

                              {transaction.user && (
                                <div className="flex items-center p-3 rounded-xl bg-neutral-50 dark:bg-neutral-900/40">
                                  <User size={16} className="text-neutral-500 dark:text-neutral-400 mr-3" />
                                  <div>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                      User (created by)
                                    </p>
                                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                      {transaction.user.fullName || transaction.user.name || "-"}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {transaction.ip_address && (
                                <div className="flex items-center p-3 rounded-xl bg-neutral-50 dark:bg-neutral-900/40">
                                  <Globe size={16} className="text-neutral-500 dark:text-neutral-400 mr-3" />
                                  <div>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                      IP address
                                    </p>
                                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                      {transaction.ip_address}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </>
                    ) : null}

                    {/* REJECTION REASON — only for rejected/failed/reversed */}
                    {["rejected", "failed", "reversed"].includes(
                      (transaction.status || "").toLowerCase()
                    ) && (
                      <motion.div variants={itemVariants} className="space-y-2 text-start">
                        <h3 className="text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-2">
                          <XCircle size={16} />
                          Rejection reason
                        </h3>
                        <div className="p-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20">
                          <p className="text-sm text-red-700 dark:text-red-300">
                            {transaction.rejectionReason ||
                              transaction.rejection_reason ||
                              "No reason was provided."}
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* NOTES */}
                    <motion.div variants={itemVariants} className="space-y-2 text-start">
                      <h3 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                        <FileText size={16} />
                        Internal notes
                      </h3>
                      <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/40">
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">
                          {transaction.notes || "No notes available for this transfer."}
                        </p>
                      </div>
                    </motion.div>
                  </motion.div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white/95 dark:bg-neutral-950/95 backdrop-blur border-t border-neutral-200 dark:border-neutral-900 px-6 py-4 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-5 h-10 border border-neutral-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-900/40 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <X size={16} />
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TransactionDetailsModal;
